import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Checkbox, Col, Divider, Flex, Form, Input, Radio, Row, Select } from 'antd';
import { FormProps, useForm } from 'antd/es/form/Form';
import { motion } from 'framer-motion';

// components
import ActionCreateHeaderComponent from 'modules/main/actions/createHeader';
import BackButton from 'components/backButton/backButton';
import FormElement from 'components/formElement/formElement';
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';
import DisabledElement from 'components/disabledElement/disabledElement';
import CompoundElement from 'components/compoundElement/compoundElement';
import SharePriceInputComponent from 'modules/main/actions/sharesPrice';
import PageTitle from 'components/title/title';
import SelectElement from 'components/selectElement/selectElement';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';

// services
import NotificationService from 'core/services/notification';
import { isRTL } from 'core/services/language';
import { onCheckSellerNationalId, onSubmitRegistryTransaction } from '../services/registryTransactionServices';
import { onCheckNationalId, onGetShareholderSharesLimit } from '../../commonServices';

// models
import { RegistryTransactionCreatePayload } from '../models/registryTransaction';
import { ActionShareholder, ShareholderSharesLimit } from '../../commonModels';
import { checkPhoneValidation } from 'utils/validators/phone';
import { mapResponseErrors } from 'utils/mapResponseErrors';
import { useNavigate } from 'react-router-dom';
import { CompanyShareholder } from 'core/models/shareholder';
import { formatErrorMessages } from 'utils/errorMessage';
import useCooperative from 'core/hooks/useCooperative';
import { useSelector } from 'react-redux';
import { RootState } from 'stores/store';

interface RegistryTransactionCreatePageProps {}

type FieldTypes = {
	seller_national_id: string;
	executive: boolean;
	share_price: string;
	shares_count: string;
	national_id: string;
	phone_country_code: string;
	phone_number: string;
	alias: string;
};

const RegistryTransactionCreatePage: FunctionComponent<RegistryTransactionCreatePageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { isCooperative } = useCooperative();

	const company = useSelector((state: RootState) => state.company);

	const [transactionForm] = useForm();

	const [seller, setSeller] = useState<CompanyShareholder>();
	const [sellerChecked, setSellerChecked] = useState(false);
	const [sellerChecking, setSellerChecking] = useState(false);

	const [buyer, setBuyer] = useState<ActionShareholder>();
	const [buyerChecked, setBuyerChecked] = useState(false);
	const [buyerChecking, setBuyerChecking] = useState(false);
	const [buyerSharesLimit, setBuyerSharesLimit] = useState<ShareholderSharesLimit>();

	// re-check the entered shares count against the limit once it loads for the buyer
	useEffect(() => {
		if (+transactionForm.getFieldValue('shares_count') > 0) transactionForm.validateFields(['shares_count']);
	}, [buyerSharesLimit]);

	const [isLoading, setIsLoading] = useState(false);

	const [limitConfirmValues, setLimitConfirmValues] = useState<FieldTypes>();
	const [limitApproved, setLimitApproved] = useState(false);

	// remaining = shareholder_max_common_shares minus what the buyer already holds
	const exceedsShareholderLimit = (values: FieldTypes) => {
		const remaining = buyerSharesLimit?.shareholder_remaining_common_shares;
		return !!buyerSharesLimit?.is_limited && remaining != null && +values.shares_count > remaining;
	};

	const truthySelectOptions = [
		{ value: false, label: t('COMMON.NO') },
		{ value: true, label: t('COMMON.YES') },
	];

	const onChangePhone = ({ number, dialCode }: PhoneInputReturn) => {
		transactionForm.setFieldsValue({
			phone_number: number,
			phone_country_code: dialCode,
		});
	};

	const validatePhone = (_: any, value: any) => {
		if (checkPhoneValidation(transactionForm.getFieldValue('phone_country_code'), value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.PHONE_INVALID')));
		else return Promise.reject();
	};

	const validateSharesCount = (_: any, value: any) => {
		if (!value || +value <= 0) return Promise.reject(new Error(t('ACTIONS.SHARES_COUNT_REQUIRED')));

		const companyRemaining = buyerSharesLimit?.company_remaining_common_shares;
		if (companyRemaining != null && +value > companyRemaining) {
			return Promise.reject(new Error(t('ACTIONS.COMPANY_SHARES_LIMIT_EXCEEDED', { count: companyRemaining.toLocaleString() as any })));
		}

		return Promise.resolve();
	};

	const validateSharePrice = (_: any, value: any) => {
		if (!value || +value <= 0) return Promise.reject(new Error(t('ACTIONS.SHARE_PRICE_REQUIRED')));
		return Promise.resolve();
	};

	// the input component emits its initial values on mount, so only validate once a field already holds a value
	const updateShareField = (name: 'shares_count' | 'share_price', value: number) => {
		const isInitialValue = transactionForm.getFieldValue(name) === undefined;
		transactionForm.setFieldValue(name, value ?? 0);
		if (!isInitialValue) transactionForm.validateFields([name]);
	};

	const sharePriceChangeHandler = ({ count, price }: { count?: number; price?: number }) => {
		if (count !== undefined) updateShareField('shares_count', count);
		if (price !== undefined) updateShareField('share_price', price);
	};

	const checkSellerNationalId = async (nationalId: string) => {
		try {
			setSellerChecking(true);

			const response = await onCheckSellerNationalId(nationalId);
			setSeller(response.company_shareholder);

			setSellerChecking(false);
			setSellerChecked(true);
			transactionForm.setFieldsValue({ seller_national_id: nationalId });
		} catch (error: any) {
			const errorMessage = error.response.data.data?.errors?.national_id?.[0] ?? error.response.data.message;
			NotificationService.error(t('COMMON.ERROR'), errorMessage ?? t('COMMON.FETCH_ERROR'));
			setSellerChecking(false);
		}
	};

	const checkBuyerNationalId = async (nationalId: string) => {
		try {
			if (seller?.shareholder.national_id === nationalId) {
				NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.SELLER_AND_BUYER_CANNOT_BE_SAME'));
				return;
			}

			setBuyerChecking(true);

			const [response, sharesLimit] = await Promise.all([onCheckNationalId(nationalId), onGetShareholderSharesLimit(nationalId)]);

			if (response.data.shareholder_exists) {
				setBuyer(response.data.shareholder);
			}

			setBuyerSharesLimit(sharesLimit);

			setBuyerChecked(true);
			transactionForm.setFieldsValue({ national_id: nationalId });
			setBuyerChecking(false);
		} catch (error: any) {
			setBuyerChecking(false);

			if (error?.status == 422) {
				NotificationService.error(t('COMMON.ERROR'), formatErrorMessages(error));
				return false;
			}

			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const onSubmit: FormProps<FieldTypes>['onFinish'] = (values) => {
		if (exceedsShareholderLimit(values)) {
			setLimitApproved(false);
			setLimitConfirmValues(values);
			return;
		}

		submitTransaction(values);
	};

	const closeLimitConfirm = () => {
		setLimitConfirmValues(undefined);
		setLimitApproved(false);
	};

	const submitTransaction = async (values: FieldTypes) => {
		try {
			setIsLoading(true);

			const payload: RegistryTransactionCreatePayload = {
				seller_national_id: values.seller_national_id,
				type: 'Common',
				share_price_common: values.share_price,
				free_common_shares_count: values.shares_count,
				national_id: values.national_id,
				executive: values.executive,
				phone_country_code: values.phone_country_code,
				phone_number: values.phone_number,
				alias: values.alias,
			};

			await onSubmitRegistryTransaction(payload);
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.REGISTRY_TRANSACTION_CREATED'));

			navigate('/dashboard/actions/registry-transactions');
		} catch (error: any) {
			setIsLoading(false);
			closeLimitConfirm();
			mapResponseErrors(error, transactionForm);
			const errorMessage = error.response.data.data?.errors?.national_id?.[0] ?? error.response.data.message;
			NotificationService.error(t('COMMON.ERROR'), errorMessage ?? t('COMMON.SUBMIT_ERROR'));
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton styles={{ paddingTop: 16 }} />

			{!sellerChecked ? (
				<ActionCreateHeaderComponent
					title={t('ACTIONS.REGISTRY_TRANSACTIONS')}
					subTitle={t('ACTIONS.FILL_TO_BUILD')}
					inputLabel={t('ACTIONS.SELLER_NATIONAL_ID')}
					onSubmit={checkSellerNationalId}
					isLoading={sellerChecking}
				/>
			) : (
				<motion.div
					initial={{ opacity: 0, transform: 'translateY(-10px)' }}
					animate={{ opacity: 1, transform: 'translateY(0)' }}
					transition={{ duration: 0.3 }}>
					<Card>
						<Form form={transactionForm} onFinish={onSubmit}>
							<div>
								<h5>{t('ACTIONS.REGISTRY_TRANSACTIONS')}</h5>
								<p className="text-light">{t('ACTIONS.FILL_TO_BUILD')}</p>

								<Row gutter={[16, 16]}>
									<Col span={24} lg={12}>
										<DisabledElement value={seller?.shareholder.national_id} label={t('ACTIONS.SELLER_NATIONAL_ID')} />
										<Form.Item name="seller_national_id" hidden>
											<Input />
										</Form.Item>
									</Col>

									<Col span={24} lg={12}>
										<DisabledElement
											value={isRTL() ? seller?.shareholder.name : (seller?.shareholder.name_en ?? seller?.shareholder.name)}
											label={t('COMMON.NAME')}
										/>
									</Col>

									<Col span={24}>
										<Divider className="my-0" />
									</Col>

									<Col span={24} lg={24}>
										<div>
											<SharePriceInputComponent
												availableShares={seller?.free_common_shares_count || 0}
												onChange={sharePriceChangeHandler}
												disabledSharePrice={isCooperative}
												sharePrice={company?.nominal_value_of_the_share}
											/>
											<Form.Item name="shares_count" className="error-only" rules={[{ validator: validateSharesCount }]}>
												<Input hidden />
											</Form.Item>

											<Form.Item name="share_price" className="error-only" rules={[{ validator: validateSharePrice }]}>
												<Input hidden />
											</Form.Item>
										</div>

										<Divider />

										<CompoundElement
											buttonText={t('COMMON.CHECK')}
											label={t('ACTIONS.BUYER_NATIONAL_ID')}
											isLoading={buyerChecking}
											onSubmit={checkBuyerNationalId}
											valueToCompare={[seller?.shareholder.national_id!]}
											compareErrorMessage={t('ACTIONS.SELLER_AND_BUYER_CANNOT_BE_SAME')}
											onChange={() => {
												setBuyerChecked(false);
												setBuyer(undefined);
												setBuyerSharesLimit(undefined);
											}}
										/>
										<Form.Item hidden rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]} name="national_id">
											<Input hidden />
										</Form.Item>
									</Col>
								</Row>

								{buyerChecked && !buyer && (
									<Row gutter={[20, 0]} className="mt-3">
										<Col span={24} md={12} lg={8}>
											<FormElement
												name="alias"
												label={t('ACTIONS.SHAREHOLDER_FULL_NAME')}
												rules={[{ required: true, message: 'Required' }]}
												type="text"
											/>
										</Col>
										<Col span={24} md={12} lg={8}>
											<Flex vertical>
												<Form.Item name="executive" rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
													<SelectElement
														label={t('COMMON.EXECUTIVE')}
														options={truthySelectOptions}
														onChange={(value) => transactionForm.setFieldsValue({ executive: value })}
													/>
												</Form.Item>
											</Flex>
										</Col>
										<Col span={24} md={12} lg={8}>
											<Form.Item
												name="phone_number"
												style={{ maxWidth: '100%' }}
												rules={[{ required: true, message: t('COMMON.PHONE_REQUIRED') }, { validator: validatePhone }]}>
												<PhoneInputComponent onChange={onChangePhone} fullWidth />
											</Form.Item>
											<Form.Item name="phone_country_code" hidden>
												<Input hidden />
											</Form.Item>
										</Col>
									</Row>
								)}

								{buyerChecked && buyer && (
									<Row className="mt-3" gutter={[16, 16]}>
										<Col span={24} lg={12}>
											<DisabledElement value={isRTL() ? buyer.name : (buyer.name_en ?? buyer.name)} label={t('COMMON.NAME')} />
										</Col>
									</Row>
								)}

								{buyerChecked && (
									<Flex justify="flex-end">
										<Button type="primary" className="px-4" htmlType="submit" loading={isLoading}>
											{t('COMMON.CREATE')}
										</Button>
									</Flex>
								)}
							</div>
						</Form>
					</Card>
				</motion.div>
			)}

			<ConfirmModalComponent
				open={!!limitConfirmValues}
				title={t('ACTIONS.SHAREHOLDER_LIMIT_EXCEEDED_TITLE')}
				note={t('ACTIONS.SHAREHOLDER_LIMIT_EXCEEDED_DESCRIPTION')}
				noteType="red"
				confirmText={t('COMMON.CONFIRM')}
				confirmDisabled={!limitApproved}
				isLoading={isLoading}
				onDiscard={closeLimitConfirm}
				onConfirm={() => submitTransaction(limitConfirmValues!)}>
				<Checkbox className="my-3" checked={limitApproved} onChange={(e) => setLimitApproved(e.target.checked)}>
					{t('ACTIONS.SHAREHOLDER_LIMIT_EXCEEDED_APPROVE')}
				</Checkbox>
			</ConfirmModalComponent>
		</>
	);
};

export default RegistryTransactionCreatePage;
